/**
 * LIVARA — product-detail buy-area state.
 * ------------------------------------------------------------------
 * The Globo Pre-order app re-renders the PDP buy row on variant change, but
 * it only reacts to a `change` on the theme's hidden input[name="id"] — which
 * product-info.js fires AFTER its section re-fetch. That makes the buttons
 * trail the swatch/pill selection by ~1s. On top of that the app caps its own
 * re-renders (advancedSettings.reRenderForm, default 3), so after a few SKU
 * clicks it stops updating entirely until the page is reloaded.
 *
 * This script owns the visible state instead, so the theme never depends on
 * the app's timing:
 *
 *   - resolves the picked SKU from the inline `data-livara-variants` map the
 *     moment an option changes (no network round-trip),
 *   - tells the app about it immediately (the same hidden input change it
 *     already listens to, with its per-product render cap cleared so it can
 *     never lock up),
 *   - marks <product-info data-livara-buy-state="normal|preorder|soldout|
 *     unavailable">. section-main-product.css uses that attribute to show
 *     exactly ONE buy row:
 *       normal      → Add to cart + Buy with Shop
 *       preorder    → Pre Order + Email me when available
 *       soldout     → Sold out + Buy with Shop (Shopify disables it)
 *       unavailable → Unavailable (no variant for the chosen combination)
 *
 * A SKU is pre-order when the Globo pre-order map (window.GloboPreorderParams
 * .preorderSettings.products[productId][variantId]) contains it — the same
 * source of truth livara-badges.js uses.
 *
 * Note on `gridProductPreOrderForm`: the app's collection initializer also
 * matches the PDP form and adds that class to it. The app then reads the
 * collection-page "back in stock" display setting (hidden) instead of the
 * product setting (button), so the Email-me row disappears after a variant
 * change. We strip the class off the single-product form; it only makes the
 * app use the correct (product) display setting.
 */
(function () {
  'use strict';

  var ATTR = 'data-livara-buy-state';
  var GRID_CLASS = 'gridProductPreOrderForm';
  var APP_CHECK_DELAY = 1800;
  var APP_CHECK_RETRY = 1200;
  var MAX_NUDGES = 1;
  var INIT_RETRIES = 8;

  var boundEl = null;
  var observer = null;
  var reapplyTimer = null;
  var appCheckTimer = null;
  var initTries = 0;
  var nudgeCount = {};

  // ---------------------------------------------------------------
  // Reading the selected variant
  // ---------------------------------------------------------------

  function getScope() {
    return document.querySelector('product-info[data-livara-variants]');
  }

  function variantsOf(el) {
    var node = el.querySelector('script[data-livara-variants]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function selectedVariantJson(el) {
    var node = el.querySelector('variant-selects [data-selected-variant]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  // Checked option values in option order (Color, then Size, ...).
  function selectedValues(el) {
    var values = [];
    el.querySelectorAll('variant-selects .product-form__input').forEach(function (group) {
      var radio = group.querySelector('input[type="radio"]:checked');
      var select = group.querySelector('select');
      if (radio) values.push(radio.value);
      else if (select) values.push(select.value);
    });
    return values;
  }

  function variantForValues(variants, values) {
    if (!variants || !values.length) return null;
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var options = [variant.option1, variant.option2, variant.option3].filter(function (option) {
        return option !== null && option !== undefined;
      });
      if (options.length !== values.length) continue;
      var matches = true;
      for (var j = 0; j < options.length; j++) {
        if (options[j] !== values[j]) {
          matches = false;
          break;
        }
      }
      if (matches) return variant;
    }
    return null;
  }

  // Variant straight from the DOM picker. null means "no variant for this
  // option combination" (only when a picker actually carries values).
  function variantFromSelection(el) {
    return variantForValues(variantsOf(el), selectedValues(el));
  }

  // Variant from the theme's rendered JSON — used for the initial state and
  // after product-info.js has re-rendered variant-selects.
  function variantFromDom(el) {
    var variants = variantsOf(el);
    if (variants && variants.length === 1) return variants[0];
    return selectedVariantJson(el);
  }

  // The remembered selection, but only while it still belongs to the variant
  // map in the DOM. If product-info.js swapped in a different product (a
  // swatch that links to another product), fall back to what the DOM says
  // instead of applying the previous product's SKU state.
  function knownVariant(el) {
    var variants = variantsOf(el);
    var remembered = el._livaraVariant;
    if (remembered && variants) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(remembered.id)) return remembered;
      }
    }
    return variantFromDom(el) || variantFromSelection(el);
  }

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------

  function isPreorder(el, variantId) {
    var params = window.GloboPreorderParams;
    var products = params && params.preorderSettings && params.preorderSettings.products;
    var productId = el.getAttribute('data-product-id');
    if (!products || !productId || variantId === null || typeof variantId === 'undefined') {
      return false;
    }
    var variantMap = products[String(productId)];
    if (!(variantMap && variantMap[String(variantId)])) return false;

    // Map membership only means "this SKU has a pre-order profile assigned".
    // The profile may only be active under certain conditions (e.g. "pre-order
    // when out of stock", or a start/end date). Ask the app for the resolved
    // profile so the theme matches what Globo actually renders — an in-stock
    // SKU on an out-of-stock-only profile is Add to cart, not Pre Order.
    var app = window.Globo && window.Globo.Preorder;
    if (app && typeof app.getResolvedProfile === 'function' && app.settings && app.settings.product) {
      try {
        var resolved = app.getResolvedProfile(app.settings.product, variantId);
        if (resolved && typeof resolved.isPreorder !== 'undefined') return !!resolved.isPreorder;
      } catch (e) {
        /* fall back to the map below */
      }
    }
    return true;
  }

  function stateFor(el, variant) {
    if (!variant) return 'unavailable';
    if (isPreorder(el, variant.id)) return 'preorder';
    return variant.available ? 'normal' : 'soldout';
  }

  // The pre-order map can still list a SKU whose profile has ended and which
  // the app therefore renders as a normal Add to cart. If the app leaves no
  // pre-order control at all for a buyable SKU, fall back to the normal row
  // instead of leaving the buy column empty.
  function effectiveState(el, variant) {
    var override = el._livaraOverride;
    if (override && variant && String(override.id) === String(variant.id)) return override.state;
    return stateFor(el, variant);
  }

  function mapReady() {
    var params = window.GloboPreorderParams;
    return !!(params && params.preorderSettings && params.preorderSettings.products);
  }

  // ---------------------------------------------------------------
  // Applying the state
  // ---------------------------------------------------------------

  function productForm(el) {
    return el.querySelector('product-form form');
  }

  function themeSubmit(el) {
    return el.querySelector(
      'product-form button.product-form__submit:not(.gPreorderBtn):not(.gPreorderNAMessage):not(.gPreorderComingSoonBtn)'
    );
  }

  function paymentButton(el) {
    return el.querySelector('product-form .shopify-payment-button');
  }

  function setSubmit(submit, available, unavailable) {
    if (!submit) return;
    var label = submit.querySelector('span');
    var strings = window.variantStrings || {};
    var text = available
      ? strings.addToCart || 'Add to cart'
      : unavailable
        ? strings.unavailable || 'Unavailable'
        : strings.soldOut || 'Sold out';
    if (available) {
      submit.removeAttribute('disabled');
      submit.removeAttribute('aria-disabled');
    } else {
      submit.setAttribute('disabled', 'disabled');
    }
    // Only touch the DOM when the label really changes — rewrite would fire
    // a childList mutation and the re-apply observer would loop on it.
    // Liquid renders the label with surrounding whitespace, hence the trim.
    if (label && label.textContent.trim() !== text) label.textContent = text;
  }

  // The app's collection initializer adds this class to the PDP form too;
  // strip it so the app uses the product-level back-in-stock display setting.
  function unmarkGrid(el) {
    var form = productForm(el);
    if (form && form.classList.contains(GRID_CLASS)) form.classList.remove(GRID_CLASS);
  }

  // classList.add/remove rewrites the class attribute even when the token is
  // already in the requested state, which the MutationObserver below would
  // treat as a new change and re-apply forever. Only touch it when needed.
  function setClass(el, className, on) {
    if (!el || el.classList.contains(className) === on) return;
    if (on) el.classList.add(className);
    else el.classList.remove(className);
  }

  function applyState(el, state) {
    if (!state) return;
    el.setAttribute(ATTR, state);
    unmarkGrid(el);

    var form = productForm(el);
    var submit = themeSubmit(el);
    var pay = paymentButton(el);

    if (state === 'normal') {
      setClass(form, 'hideBuyNowBtn', false);
      setClass(submit, 'gPreorderHidden', false);
      setClass(pay, 'gPreorderHidden', false);
      setSubmit(submit, true, false);
    } else if (state === 'preorder') {
      // Hide the theme buy row instantly; the app injects its Pre Order
      // button into the same form a moment later. We deliberately leave the
      // app's button/messages alone — they own their own visibility. The
      // gPreorderHidden class is also removed by the app when it clones the
      // button, so this cannot hide the Pre Order button.
      setClass(form, 'hideBuyNowBtn', true);
      setClass(submit, 'gPreorderHidden', true);
      setClass(pay, 'gPreorderHidden', true);
    } else if (state === 'soldout') {
      // Both in-stock and sold-out SKUs keep the Shop Pay row below the
      // primary button (per the PDP spec). Shopify marks the accelerated
      // checkout button disabled/non-clickable for an unavailable variant on
      // its own, so we only have to stop hiding it.
      setClass(form, 'hideBuyNowBtn', false);
      setClass(submit, 'gPreorderHidden', false);
      setClass(pay, 'gPreorderHidden', false);
      setSubmit(submit, false, false);
    } else {
      setClass(form, 'hideBuyNowBtn', true);
      setClass(submit, 'gPreorderHidden', false);
      setClass(pay, 'gPreorderHidden', true);
      setSubmit(submit, false, true);
    }
  }

  // ---------------------------------------------------------------
  // Talking to the Globo app
  // ---------------------------------------------------------------

  function clearRenderCap(el) {
    var productId = el.getAttribute('data-product-id');
    if (!productId) return;
    var key = 'gpo_render_' + productId;
    try {
      delete window[key];
    } catch (e) {
      window[key] = undefined;
    }
  }

  function notifyApp(el, variant) {
    var input = el.querySelector('product-form input[name="id"]');
    if (!input || !variant || variant.id === null || typeof variant.id === 'undefined') return;
    input.value = String(variant.id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Keep the address bar on the SKU the shopper just picked. product-info.js
  // does this after its section fetch; doing it immediately means reloading
  // during that fetch cannot land back on the previous (e.g. pre-order) SKU.
  // The same `?variant=` form product-info.js uses.
  function updateUrl(el, variant) {
    if (!variant || variant.id === null || typeof variant.id === 'undefined') return;
    if (el.getAttribute('data-update-url') === 'false') return;
    var url = el.getAttribute('data-url');
    if (!url) return;
    try {
      window.history.replaceState({}, '', url + '?variant=' + variant.id);
    } catch (e) {
      /* history API unavailable — the URL update is a nicety, not required */
    }
  }

  function nudge(el) {
    var variant = el._livaraVariant;
    if (!variant || variant.id === null || typeof variant.id === 'undefined') return;
    var key = String(variant.id);
    nudgeCount[key] = (nudgeCount[key] || 0) + 1;
    if (nudgeCount[key] > MAX_NUDGES) return;
    unmarkGrid(el);
    clearRenderCap(el);
    notifyApp(el, variant);
  }

  // The app builds the Pre Order button and the Email-me row asynchronously
  // (~150ms + a lazy chunk). If they are still missing for a pre-order SKU,
  // give it one more chance. If a buyable SKU ends up with no pre-order
  // control at all, fall back to the normal row so the product is never left
  // with an empty buy column. Out-of-stock SKUs intentionally have no Email-me
  // row, so there is nothing to wait for in that state.
  function appControls(el) {
    return el.querySelector('button.gPreorderBtn, .gPreorderNAMessage, .gPreorderComingSoonBtn');
  }

  function checkAppState(el, state, attempt) {
    if (el.getAttribute(ATTR) !== state) return;
    if (state !== 'preorder') return;

    if (appControls(el)) {
      if (!el.querySelector('#Globo-Back-In-Stock')) nudge(el);
      return;
    }
    if (attempt < MAX_NUDGES) {
      nudge(el);
      appCheckTimer = setTimeout(function () {
        checkAppState(el, state, attempt + 1);
      }, APP_CHECK_RETRY);
      return;
    }
    var variant = el._livaraVariant;
    if (variant && variant.available) {
      el._livaraOverride = { id: variant.id, state: 'normal' };
      applyState(el, 'normal');
    }
  }

  function scheduleAppCheck(el, state) {
    clearTimeout(appCheckTimer);
    appCheckTimer = setTimeout(function () {
      checkAppState(el, state, 0);
    }, APP_CHECK_DELAY);
  }

  // ---------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------

  function onSelectionChange(event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('variant-selects')) return;
    var el = getScope();
    if (!el || !el.contains(target)) return;
    // A swatch that links to a different product: let product-info.js swap
    // the section; the new product's map is handled after the swap.
    if (
      target.dataset &&
      target.dataset.productUrl &&
      el.getAttribute('data-url') &&
      target.dataset.productUrl !== el.getAttribute('data-url')
    ) {
      return;
    }

    var variant = variantFromSelection(el);
    var state = variant ? stateFor(el, variant) : 'unavailable';

    delete nudgeCount[variant ? String(variant.id) : 'unavailable'];
    el._livaraOverride = null;
    if (variant) el._livaraVariant = variant;

    applyState(el, state);

    // product-info.js resets the form synchronously in its own change
    // handler (resetProductFormState -> submit disabled) before it fetches
    // the section. Re-assert once that has run, so the newly picked SKU's row
    // is already usable instead of greying out for the duration of the fetch.
    setTimeout(function () {
      if (el.getAttribute(ATTR) === state) applyState(el, state);
    }, 0);

    if (variant) {
      clearRenderCap(el);
      notifyApp(el, variant);
      updateUrl(el, variant);
      scheduleAppCheck(el, state);
    }
  }

  function onVariantChange(event) {
    var el = getScope();
    if (!el) return;
    var data = event && event.data;
    var sectionId = el.getAttribute('data-section');
    if (data && data.sectionId && sectionId && data.sectionId !== sectionId) return;

    var variant = (data && data.variant) || variantFromDom(el);
    if (!variant) return;
    el._livaraVariant = variant;
    applyState(el, effectiveState(el, variant));
  }

  // Re-assert the current state after the app (or product-info.js) mutates
  // the buy area. The state is always derived from the last selection we
  // recorded — never from the DOM, which can still show the previous SKU
  // while the section fetch is in flight.
  function scheduleReapply() {
    clearTimeout(reapplyTimer);
    reapplyTimer = setTimeout(function () {
      var el = getScope();
      if (!el || !el.getAttribute(ATTR)) return;
      unmarkGrid(el);
      var variant = knownVariant(el);
      if (!variant) return;
      el._livaraVariant = variant;
      applyState(el, effectiveState(el, variant));
    }, 80);
  }

  function bindObserver(el) {
    if (observer) observer.disconnect();
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (target && target.classList && target.classList.contains(GRID_CLASS)) {
          target.classList.remove(GRID_CLASS);
        }
      }
      scheduleReapply();
    });
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function refresh() {
    var el = getScope();
    if (!el) return;

    var variant = knownVariant(el);
    if (!variant) {
      if (initTries++ < INIT_RETRIES) setTimeout(refresh, 300);
      return;
    }
    // The app inlines its preorder map before deferred scripts run; if it is
    // late, wait a little so a pre-order SKU is not briefly shown as normal.
    if (!mapReady() && initTries++ < INIT_RETRIES) {
      setTimeout(refresh, 300);
      return;
    }
    initTries = 0;

    el._livaraVariant = variant;
    var state = effectiveState(el, variant);
    applyState(el, state);

    if (el !== boundEl) {
      boundEl = el;
      bindObserver(el);
    }
    scheduleAppCheck(el, state);
  }

  document.addEventListener('change', onSelectionChange, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  }

  document.addEventListener('product-info:loaded', refresh);
  document.addEventListener('shopify:section:load', refresh);

  window.addEventListener('load', function () {
    setTimeout(refresh, 300);
    setTimeout(refresh, 1500);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  window.LivaraBuyState = {
    refresh: refresh,
    get state() {
      var el = getScope();
      return el ? el.getAttribute(ATTR) : null;
    },
  };
})();
